import { useEffect, useState } from 'react';
import { AtSign, Heart, MessageCircle, Repeat2, UserPlus } from 'lucide-react';
import { gistNoticeText, type GistNoticeKind, type GistNoticeView } from '@london-life/contracts';
import { describeError } from '@london-life/errors';
import { api } from '../lib/api';
import { notify } from '../lib/alerts';
import { ago } from '../lib/format';
import { GistAvatar, GistEmpty, type GistNav } from './parts';

const ICONS: Record<GistNoticeKind, { icon: typeof Heart; tone: string }> = {
  like: { icon: Heart, tone: 'like' },
  comment_like: { icon: Heart, tone: 'like' },
  regist: { icon: Repeat2, tone: 'regist' },
  follow: { icon: UserPlus, tone: 'people' },
  reply: { icon: MessageCircle, tone: 'people' },
  mention: { icon: AtSign, tone: 'people' },
};

// Gist's notifications, as Lagos: likes, replies, re-gists, follows and mentions (likes and re-gists of one post
// grouped); opening them clears the badge. A tap opens the post, or the follower's page.
export function GistNotices({ nav, onSeen }: { nav: GistNav; onSeen: () => void }) {
  const [items, setItems] = useState<GistNoticeView[] | null>(null);
  useEffect(() => {
    let live = true;
    api.gist
      .notices()
      .then((reply) => {
        if (!live) return;
        setItems(reply.items);
        onSeen();
        void api.gist.seen().catch(() => {});
      })
      .catch((error: unknown) => {
        if (!live) return;
        setItems([]);
        notify(describeError(error).message);
      });
    return () => {
      live = false;
    };
  }, [onSeen]);
  if (!items) return <p className="gist-loading">Loading your notifications…</p>;
  if (!items.length)
    return (
      <GistEmpty title="Nothing to see here yet" body="Likes, replies, re-gists, follows and mentions show up here." />
    );
  return (
    <ul className="gist-notices" aria-label="Notifications">
      {items.map((item) => {
        const { icon: Icon, tone } = ICONS[item.kind];
        const [who, ...rest] = gistNoticeText(item).split(' ');
        const open = () =>
          item.postId ? nav.post(item.postId) : item.actors[0] && nav.user(item.actors[0].lifeId, item.actors[0].name);
        return (
          <li key={`${item.kind}:${item.postId}:${item.commentId}:${item.at}`} className={item.unread ? 'unread' : ''}>
            <button type="button" onClick={open}>
              <Icon className={`gist-notice-icon ${tone}`} aria-hidden="true" />
              <span className="gist-notice-main">
                <span className="gist-notice-faces">
                  {item.actors.map((actor) => (
                    <GistAvatar key={actor.lifeId} author={actor} size={28} />
                  ))}
                </span>
                <span>
                  <strong>{who}</strong> {rest.join(' ')} <small>· {ago(item.at)}</small>
                </span>
                {item.snippet && <small className="gist-notice-snippet">{item.snippet}</small>}
              </span>
            </button>
          </li>
        );
      })}
    </ul>
  );
}
